/**
 * Foreign / Whale switch.
 *
 * These are two different analyses of the same instrument, not two views of
 * one payload — Foreign reads reported foreign buy/sell, Whale clusters broker
 * summaries — so switching refetches from the API rather than re-slicing
 * anything already in the browser.
 *
 * The backend calls them `foreign` and `broker`; the desk calls the second
 * one Whale. The wire value stays `broker` and only the label changes, so the
 * URL, the cache key and the API all agree.
 */
import type { AnalysisMethod } from "../api/client.ts";

const OPTIONS: ReadonlyArray<{ method: AnalysisMethod; label: string; hint: string }> = [
	{ method: "broker", label: "Whale", hint: "Broker-summary clustering" },
	{ method: "foreign", label: "Foreign", hint: "Reported foreign buy/sell" },
];

interface FlowToggleProps {
	method: AnalysisMethod;
	onChange: (method: AnalysisMethod) => void;
	/** True while the newly selected method is still in flight. */
	busy?: boolean;
}

export function FlowToggle({ method, onChange, busy = false }: FlowToggleProps) {
	return (
		<div className="flow-toggle" role="group" aria-label="Flow method">
			{OPTIONS.map((option) => {
				const selected = option.method === method;
				return (
					<button
						key={option.method}
						type="button"
						className="flow-toggle__option"
						// aria-pressed, not aria-current: this is a two-state control,
						// and the pressed one is also named in the button text.
						aria-pressed={selected}
						aria-describedby={undefined}
						title={option.hint}
						data-selected={selected}
						data-busy={selected && busy}
						onClick={() => onChange(option.method)}
					>
						{option.label}
					</button>
				);
			})}
		</div>
	);
}
